import { axiosPrivate } from 'api/client/classes/AxiosInstance';
import PropTypes from 'prop-types';
import React, {
  createContext,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  applyStoredSecurityState,
  getStoredSecurityState,
  normalizeScopes,
  storeSecurityState,
  storeUserId
} from 'utils/auth';
import { createLogger } from 'utils/logger';
import { getItemFromStore } from 'utils/utils';

const logger = createLogger('AuthenticationProvider');

/**
 * The authentication settings object.
 *
 * @property {string|null} accessToken - The access token
 * @property {string[]} emails - The list of emails
 * @property {string|null} id - The ID
 * @property {boolean} isAdmin - Indicates if the user is an admin
 * @property {boolean} persist - Indicates if the session should persist
 * @property {boolean} persistLoader - Indicates if the loader should persist
 * @property {boolean} refreshing - Indicates if the settings are being refreshed
 * @property {string[]} roles - The list of roles
 * @property {string[]} scopes - The list of scopes
 * @property {Object} security - The security settings
 * @property {Object} session - The session settings
 * @property {boolean} showIdleModal - Indicates if the idle modal should be shown
 * @property {string} stage - The authentication stage

 * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
 * @since 0.1.0-beta.1
 * @version 0.1.0-beta.7
 */
export const authenticationSettings = {
  accessToken: null,
  emails: [],
  id: '',
  isAdmin: false,
  forbidden: false,
  persist: true,
  persistLoader: true,
  refreshing: false,
  roles: [],
  scopes: [],
  scopesSelected: [],
  security: {},
  session: {},
  showIdleModal: false,
  idleModalShownAt: null,
  idleGracePeriodMs: null,
  lockCountdown: null,
  stage: 'authenticated'
};

/// Retrieve stored security state from local storage or other storage mechanism
const storedSecurity = getStoredSecurityState();

/**
 * Authentication state object that combines authentication settings with stored security data.
 * @typedef {Object} AuthenticationState
 * @property {Array<string>} scopesSelected - List of selected authentication scopes, retrieved from stored security or defaults to empty array
 * @property {Object} session - Session information
 * @property {Object} session.device - Device-related session information
 * @property {boolean} session.device.isVerified - Whether the current device is verified
 * @property {boolean} session.device.sessionLocked - Whether the current session is locked
 * @property {*} - Includes all properties from authenticationSettings

 * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
 * @since 0.1.0-beta.1
 * @version 0.1.0-beta.7
 */
const authenticationState = {
  ...authenticationSettings,
  scopesSelected: storedSecurity.scopesSelected || [],
  session: {
    ...authenticationSettings.session,
    device: {
      isVerified: storedSecurity.deviceVerified,
      sessionLocked: storedSecurity.sessionLocked
    }
  }
};

/**
 * Create AuthenticationContext object.
 *
 * @type {React.Context<{}>}
 *
 * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
 * @version 0.1.0-beta.1
 * @since 0.1.0-beta.1
 */
export const AuthenticationContext = createContext(authenticationSettings);

/**
 * AuthenticationProvider component that provides authentication context to its children
 * Optimized with memoization to prevent unnecessary re-renders
 *
 * @component
 * @param {Object} props - The component props
 * @returns {JSX.Element} The rendered AuthenticationProvider component
 *
 * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
 * @version 0.1.0-beta.7
 * @since 0.1.0-beta.1
 */
const AuthenticationProvider = memo(({ children }) => {
  // Initialize from stored values
  const [authentication, setAuthentication] = useState(authenticationState);
  const [persist, setPersist] = useState(getItemFromStore('persist') || false);
  const [globalLoader, setGlobalLoader] = useState(false);
  // Track token refresh status to avoid race conditions
  const refreshInProgressRef = useRef(false);
  const lastTokenUpdateRef = useRef(0);
  const tokenUpdateDelay = 300; // ms to delay between token updates

  // Register token refresh listener
  useEffect(() => {
    logger.debug('Setting up token refresh listener');

    // Register with axiosPrivate to be notified when tokens are refreshed
    const unsubscribe = axiosPrivate.onTokenRefresh(authData => {
      logger.debug('Received token refresh notification from Axios');

      if (authData && authData.accessToken) {
        // Store user ID in localStorage for better persistence
        if (authData.id) {
          storeUserId(authData.id);
        }

        // Prevent multiple token refreshes in quick succession
        const now = Date.now();
        if (now - lastTokenUpdateRef.current < tokenUpdateDelay) {
          logger.debug(
            'Throttling token update - too soon after previous update'
          );
          return;
        }

        // Set refresh in progress flag to prevent duplicate updates
        if (refreshInProgressRef.current) {
          logger.debug('Token refresh already in progress, queueing update');
          setTimeout(() => {
            // Try again after a short delay
            axiosPrivate.notifyOnTokenRefresh(authData);
          }, tokenUpdateDelay);
          return;
        }

        refreshInProgressRef.current = true;
        lastTokenUpdateRef.current = now;

        // Update auth state with refreshed data
        setAuthentication(prevState => {
          const storedSecurityState = getStoredSecurityState();

          // Check if we're already using this token to avoid unnecessary updates
          if (prevState.accessToken === authData.accessToken) {
            logger.debug('Token already set, skipping update');
            refreshInProgressRef.current = false;
            return prevState;
          }

          logger.debug('Updating authentication state with new token data');

          const updatedState = {
            ...prevState,
            ...authData,
            accessToken: authData.accessToken,
            refreshing: false,
            persistLoader: false,
            id: authData.id || prevState.id, // Ensure we keep or update user ID
            security: {
              ...(prevState.security || {}),
              ...(authData.security || {}),
              multifactor: {
                ...(prevState.security?.multifactor || {}),
                ...(authData.security?.multifactor || {}),
                app: {
                  ...(prevState.security?.multifactor?.app || {}),
                  ...(authData.security?.multifactor?.app || {}),
                  isEnabled:
                    authData.security?.multifactor?.app?.isEnabled ||
                    prevState.security?.multifactor?.app?.isEnabled ||
                    storedSecurityState.mfaEnabled
                }
              }
            },
            session: {
              ...(prevState.session || {}),
              ...(authData.session || {}),
              device: {
                ...(prevState.session?.device || {}),
                ...(authData.session?.device || {}),
                isVerified:
                  authData.session?.device?.isVerified ||
                  prevState.session?.device?.isVerified ||
                  storedSecurityState.deviceVerified,
                sessionLocked:
                  authData.session?.device?.sessionLocked ||
                  prevState.session?.device?.sessionLocked ||
                  storedSecurityState.sessionLocked
              }
            }
          };

          // Log the auth update to help with debugging
          logger.debug('Authentication updated from refresh:', {
            hadAuthBefore: !!prevState.id,
            hasAuthNow: !!updatedState.id,
            tokenChanged: prevState.accessToken !== updatedState.accessToken
          });

          // Release the refresh flag after a small delay to prevent multiple updates
          setTimeout(() => {
            refreshInProgressRef.current = false;
          }, 100);

          return updatedState;
        });
      } else {
        logger.warn('Received token refresh with missing data', {
          hasToken: !!authData?.accessToken,
          hasUserId: !!authData?.id
        });
        refreshInProgressRef.current = false;
      }
    });

    // Enhanced debug logging at startup
    logger.debug('Auth provider mounted, refresh listener active');

    return () => {
      logger.debug('Removing token refresh listener');
      unsubscribe();
    };
  }, []);

  // Store user ID when it changes
  useEffect(() => {
    if (authentication?.id) {
      storeUserId(authentication.id);
    }
  }, [authentication?.id]);

  /**
   * Disable persist loader when no prior session exists
   * This prevents long hangs on protected routes for logged-out users.
   */
  useEffect(() => {
    let hasStoredUserId = false;
    try {
      hasStoredUserId = !!localStorage.getItem('auth_user_id');
    } catch (e) {
      // ignore storage access issues
    }

    if (
      !authentication.id &&
      !hasStoredUserId &&
      authentication.persistLoader
    ) {
      setAuthentication(prev => ({
        ...prev,
        persistLoader: false,
        refreshing: false
      }));
    }
  }, [authentication.id, authentication.persistLoader, setAuthentication]);

  /**
   * Updates the authentication state.
   *
   * @param {Object|Function} updater - If an object, it will be merged with the previous state.
   *                                  If a function, it will be called with the previous state to generate a new state.
   *
   * @example
   * // Update with an object
   * updateAuthentication({ id: 'user123', isAuthenticated: true });
   *
   * // Update with a function
   * updateAuthentication(prevState => ({ ...prevState, lastLogin: new Date() }));
   *
   * @returns {void} The function doesn't return a value but updates the authentication state.
   *
   * @description
   * This function handles:
   * - Merging or generating new authentication state
   * - Storing/clearing user ID when it changes
   * - Normalizing scopes if they're included in the update

 * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
 * @since 0.1.0-beta.1
 * @version 0.1.0-beta.7
   */
  const updateAuthentication = useCallback(updater => {
    setAuthentication(prevState => {
      // If updater is a function, call it with prevState
      // Otherwise, merge the updater object with prevState
      const newState =
        typeof updater === 'function'
          ? updater(prevState)
          : { ...prevState, ...updater };

      // Store user ID if present
      if (newState.id && newState.id !== prevState.id) {
        storeUserId(newState.id);
      } else if (!newState.id && prevState.id) {
        // Clear user ID on logout
        storeUserId(null);
      }

      // Normalize scopes if they're present in the update
      if (newState.scopesSelected !== undefined) {
        try {
          newState.scopesSelected = normalizeScopes(newState.scopesSelected);
        } catch (error) {
          logger.error('Error normalizing scopes:', error);
        }
      }

      return newState;
    });
  }, []);

  /**
   * Set isAdmin flag if user has admin or super-admin role.
   */
  useEffect(() => {
    if (Array.isArray(authentication.roles)) {
      const hasMatchingRole = authentication.roles.some(role =>
        ['admin', 'super-admin'].includes(role.slug)
      );

      if (hasMatchingRole !== authentication.isAdmin) {
        updateAuthentication({ isAdmin: hasMatchingRole });
      }
    }
  }, [authentication.roles, authentication.isAdmin, updateAuthentication]);

  /**
   * Store security state when it changes
   */
  useEffect(() => {
    // Store security state when relevant fields change
    storeSecurityState(authentication);
  }, [
    authentication.security?.multifactor?.app?.isEnabled,
    authentication.session?.device?.isVerified,
    authentication.session?.device?.sessionLocked,
    authentication.scopesSelected
  ]);

  /**
   * Apply stored security state when authentication changes
   * This ensures that security settings are preserved during updates
   */
  useEffect(() => {
    // Only apply if we have authentication data
    if (authentication.id) {
      updateAuthentication(prevState => {
        return applyStoredSecurityState(prevState);
      });
    }
  }, [authentication.id, updateAuthentication]);

  // Set the auth token for axiosPrivate whenever it changes
  useEffect(() => {
    if (authentication.accessToken) {
      axiosPrivate.setAuthToken(authentication.accessToken);

      // Update token in local ref to track when it was last set
      lastTokenUpdateRef.current = Date.now();
    } else {
      axiosPrivate.removeAuthToken();
    }
  }, [authentication.accessToken]);

  /**
   * Utility object for token management operations.
   *
   * @typedef {Object} TokenAPI
   * @property {function(): Promise<boolean>} refreshToken - Attempts to refresh the authentication token.
   * Returns true if refresh was initiated or false if a refresh was already in progress.
   * Sets refreshing state during the process and handles errors.
   * @property {function(): void} clearToken - Removes the authentication token from axios instance
   * and updates the authentication state.
   * @property {function(): string|null} getToken - Returns the current access token.
   *
   * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
   * @since 0.1.0-beta.7
   * @version 0.1.0-beta.7
   */
  const tokenAPI = useMemo(
    () => ({
      refreshToken: async () => {
        try {
          // Only attempt refresh if we're not already refreshing
          if (!refreshInProgressRef.current) {
            refreshInProgressRef.current = true;
            updateAuthentication(prev => ({ ...prev, refreshing: true }));

            // Use the axiosPrivate forceRefresh method
            const result = await axiosPrivate.forceRefresh();

            // If no result was returned, the refresh was handled by the interceptor
            if (!result) {
              updateAuthentication(prev => ({
                ...prev,
                refreshing: false,
                persistLoader: false
              }));
            }

            return true;
          }
          return false;
        } catch (error) {
          logger.error('Error in manual token refresh:', error);
          updateAuthentication(prev => ({
            ...prev,
            refreshing: false,
            persistLoader: false
          }));
          return false;
        } finally {
          setTimeout(() => {
            refreshInProgressRef.current = false;
          }, 100);
        }
      },
      clearToken: () => {
        axiosPrivate.removeAuthToken();
        updateAuthentication(prev => ({ ...prev, accessToken: null }));
      },
      getToken: () => authentication.accessToken
    }),
    [authentication.accessToken, updateAuthentication]
  );

  /**
   * Memoized authentication state derived from the authentication object.
   * Contains commonly accessed properties to avoid unnecessary re-renders in consuming components.
   *
   * @type {Object}
   * @property {boolean} isAdmin - Indicates if the current user has admin privileges
   * @property {Array<string>} scopesSelected - Selected scopes for the current user
   * @property {Array<Object>} roles - Roles assigned to the current user
   * @property {Array<string>} scopes - All available scopes for the current user
   *
   * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
   * @version 0.1.0-beta.7
   * @since 0.1.0-beta.7
   */
  const authState = useMemo(() => {
    const {
      isAdmin,
      scopesSelected,
      roles,
      scopes
      // Extract other commonly accessed properties
    } = authentication;

    return {
      isAdmin,
      scopesSelected,
      roles,
      scopes
      // Include other derived properties that consumers often access
    };
  }, [authentication]);

  /**
   * Memoized authentication context value object that combines authentication state with additional functionality.
   *
   * @type {Object} The authentication context value
   * @property {Object} authentication - Combined authentication state with frequently accessed properties
   * @property {boolean} persist - Whether authentication should persist across sessions
   * @property {Function} setAuthentication - Function to update authentication state
   * @property {Function} setPersist - Function to toggle persistence setting
   * @property {boolean} globalLoader - Global loading state indicator
   * @property {Function} setGlobalLoader - Function to update global loading state
   * @property {Object} tokenAPI - Token management API
   * @memberof AuthenticationProvider
   *
   * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
   * @version 0.1.0-beta.7
   * @since 0.1.0-beta.7
   */
  const authContextValue = useMemo(
    () => ({
      authentication: {
        ...authentication,
        // Replace frequently accessed properties with their memoized versions
        ...authState
      },
      persist,
      setAuthentication: updateAuthentication, // Use the memoized update function
      setPersist,
      globalLoader,
      setGlobalLoader,
      tokenAPI // Add token management API
    }),
    [
      authentication,
      persist,
      globalLoader,
      authState,
      updateAuthentication,
      tokenAPI
    ]
  );

  // Use React.memo around children to prevent re-renders when context doesn't change
  const memoizedChildren = useMemo(() => children, [children]);

  return (
    <AuthenticationContext.Provider value={authContextValue}>
      {memoizedChildren}
    </AuthenticationContext.Provider>
  );
});

AuthenticationProvider.propTypes = {
  children: PropTypes.node
};

export default AuthenticationProvider;
